/**
 * Programme-level grievance channel (ESS Policy v1.0 cl. 0106–0124; MCC-700
 * v3.1 §6.1.4). Shared vocabulary for the public page, the API route and the
 * staff register.
 *
 * Timelines (ESS cl. 0113; MCC-700 §6.1.4.1): acknowledged in writing within
 * 30 days; programme-level investigation within 60 days; escalation to the
 * board, then independent dispute resolution. An open grievance on a project
 * blocks issuance (MCC-100 cl. 0162(6), enforced in the database).
 */

export const GRIEVANCE_CATEGORIES = ['ENVIRONMENTAL', 'SOCIAL', 'GOVERNANCE', 'METHODOLOGY', 'PROCESS', 'OTHER'] as const;
export type GrievanceCategory = (typeof GRIEVANCE_CATEGORIES)[number];
export const CATEGORY_LABELS: Record<GrievanceCategory, string> = {
  ENVIRONMENTAL: 'Environmental harm or risk',
  SOCIAL: 'Social impact, rights, labour, FPIC',
  GOVERNANCE: 'Programme governance or conflict of interest',
  METHODOLOGY: 'Methodology, quantification or verification',
  PROCESS: 'A project grievance mechanism that is inaccessible or unresponsive',
  OTHER: 'Other',
};

export const GRIEVANCE_STATUSES = ['RECEIVED', 'ACKNOWLEDGED', 'UNDER_INVESTIGATION', 'ESCALATED', 'RESOLVED', 'CLOSED'] as const;
export type GrievanceStatus = (typeof GRIEVANCE_STATUSES)[number];
export const STATUS_LABELS: Record<GrievanceStatus, string> = {
  RECEIVED: 'Received',
  ACKNOWLEDGED: 'Acknowledged',
  UNDER_INVESTIGATION: 'Under investigation',
  ESCALATED: 'Escalated',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
};
export const OPEN_STATUSES: readonly GrievanceStatus[] = ['RECEIVED', 'ACKNOWLEDGED', 'UNDER_INVESTIGATION', 'ESCALATED'];

export const ACKNOWLEDGE_DAYS = 30;
export const INVESTIGATE_DAYS = 60;

export const LIMITS = { subject: 200, description: 8000, contactName: 200, contactEmail: 320 } as const;

export function isCategory(v: unknown): v is GrievanceCategory {
  return typeof v === 'string' && (GRIEVANCE_CATEGORIES as readonly string[]).includes(v);
}
export function isStatus(v: unknown): v is GrievanceStatus {
  return typeof v === 'string' && (GRIEVANCE_STATUSES as readonly string[]).includes(v);
}

export interface PublicGrievance {
  id: string;
  reference: string;
  projectId: string | null;
  category: GrievanceCategory;
  status: GrievanceStatus;
  anonymous: boolean;
  publicSummary: string | null;
  createdAt: string;
  acknowledgedAt: string | null;
  resolvedAt: string | null;
}

export function daysSince(iso: string, now = new Date()): number {
  return Math.floor((now.getTime() - new Date(iso).getTime()) / 86_400_000);
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
